/**
 * An enumeration of valid Mark Properties Channels.
 *
 * @see https://vega.github.io/vega/docs/marks/
 * @see https://vega.github.io/vega/docs/scales/
 */
const MARK_PROPERTY_CHANNEL_ENUM = Object.freeze({
  X: 'x',
  Y: 'y',
  X2: 'x2',
  Y2: 'y2',
  COLOR: 'color',
  OPACITY: 'opacity',
  SHAPE: 'shape',
  SIZE: 'size',
  TEXT: 'text',
  GEOPOINT: 'geopoint',
  // Not doing row/column faceting yet
});

const TEMPORAL_AXIS_LABEL_FORMAT_ENUM = Object.freeze({
  YEAR: '%Y',
  DATE: '%b %-d, %Y',
  DATE_TIME: '%b %-d, %Y %H:%M:%S',
});

const INTENT_ENUM = Object.freeze({
  SERIES: 'series',
  VALUE_X: 'x-value',
  VALUE_Y: 'y-value',
  AXIS_VALUE: 'value-axis',
  AXIS_CATEGORY: 'category-axis',
});

const VALUE_INTENTS = [
  INTENT_ENUM.VALUE_X,
  INTENT_ENUM.VALUE_Y,
  INTENT_ENUM.AXIS_VALUE,
];

const SORT_CHANNEL_ENUM = Object.freeze({
  CATEGORY: 'Category',
  VALUE: 'Aggregated Value',
});

const SORT_CHANNEL_PRESENTATION = Object.freeze({
  [SORT_CHANNEL_ENUM.CATEGORY]: 'Category',
  [SORT_CHANNEL_ENUM.VALUE]: 'Value',
});

const SORT_ORDER_ENUM = Object.freeze({
  ASC: 'ascending',
  DESC: 'descending',
});

/**
 * Bundle of all valid Vega channel types.
 *
 * @see https://vega.github.io/vega/docs/marks/
 * @see https://vega.github.io/vega/docs/scales/
 */
const CHART_CHANNEL_ENUM = Object.freeze(
  Object.assign({}, MARK_PROPERTY_CHANNEL_ENUM)
);

/**
 * An enumeration of the valid Data Type, or Measurement values.
 *
 * @see https://vega.github.io/vega/docs/scales/
 */
const MEASUREMENT_ENUM = Object.freeze({
  NOMINAL: 'nominal',
  ORDINAL: 'ordinal',
  QUANTITATIVE: 'quantitative',
  TEMPORAL: 'temporal',
  GEOSPATIAL: 'geospatial',
  GEOSPATIAL_ARRAY: 'geospatial_array',
});

/**
 * Types of possible icons
 */
const ICON_TYPE_ENUM = Object.freeze({
  FONT_AWESOME: 'fa',
  SVG: 'svg',
});

/**
 * Icons to measurement mapping
 */
const MEASUREMENT_ICON_ENUM = Object.freeze({
  [MEASUREMENT_ENUM.NOMINAL]: {
    type: ICON_TYPE_ENUM.FONT_AWESOME,
    icon: 'font',
  },
  [MEASUREMENT_ENUM.ORDINAL]: {
    type: ICON_TYPE_ENUM.FONT_AWESOME,
    icon: 'sort-amount-asc',
  },
  [MEASUREMENT_ENUM.QUANTITATIVE]: {
    type: ICON_TYPE_ENUM.FONT_AWESOME,
    icon: 'hashtag',
  },
  [MEASUREMENT_ENUM.TEMPORAL]: {
    type: ICON_TYPE_ENUM.FONT_AWESOME,
    icon: 'calendar',
  },
  [MEASUREMENT_ENUM.GEOSPATIAL]: {
    type: ICON_TYPE_ENUM.SVG,
    icon: 'geoglobe',
  },
  [MEASUREMENT_ENUM.GEOSPATIAL_ARRAY]: {
    type: ICON_TYPE_ENUM.SVG,
    icon: 'geonumber',
  },
});

const INFERRED_TYPE_ENUM = Object.freeze({
  ARRAY: 'Array',
  NUMBER: 'Number',
  DATE: 'Date',
  STRING: 'String',
  GEOPOINT: 'GeoPoint',
});

const INFERRED_TYPE_ICON_ENUM = Object.freeze({
  [INFERRED_TYPE_ENUM.NUMBER]: {
    type: ICON_TYPE_ENUM.FONT_AWESOME,
    icon: 'hashtag',
  },
  [INFERRED_TYPE_ENUM.DATE]: {
    type: ICON_TYPE_ENUM.FONT_AWESOME,
    icon: 'calendar',
  },
  [INFERRED_TYPE_ENUM.ARRAY]: {
    type: ICON_TYPE_ENUM.FONT_AWESOME,
    icon: 'font',
  },
  [INFERRED_TYPE_ENUM.STRING]: {
    type: ICON_TYPE_ENUM.FONT_AWESOME,
    icon: 'font',
  },
  [INFERRED_TYPE_ENUM.GEOPOINT]: {
    type: ICON_TYPE_ENUM.SVG,
    icon: 'geoglobe',
  },
});

const REFERERCE_TYPES_ENUM = Object.freeze({
  ARRAY: 'Array',
  DOCUMENT: 'Document',
});

const CHANNEL_TYPE_ENUM = Object.freeze({
  CATEGORY: 'category',
  AGGREGATION: 'aggregation',
  VALUE: 'value',
  NUMBER: 'number',
  GEOPOINT: 'geopoint',
});

export {
  ICON_TYPE_ENUM,
  CHART_CHANNEL_ENUM,
  MEASUREMENT_ENUM,
  MEASUREMENT_ICON_ENUM,
  INTENT_ENUM,
  VALUE_INTENTS,
  SORT_CHANNEL_ENUM,
  SORT_CHANNEL_PRESENTATION,
  SORT_ORDER_ENUM,
  INFERRED_TYPE_ENUM,
  CHANNEL_TYPE_ENUM,
  INFERRED_TYPE_ICON_ENUM,
  REFERERCE_TYPES_ENUM,
  TEMPORAL_AXIS_LABEL_FORMAT_ENUM,
};
